import {useCallback, useEffect, useRef, useState} from "react";

import useIsUnmounted from "./useIsUnmounted";
import {isDeepEqual} from "../equality/equalityUtils";

/**
 * It works similarly to useState however whenever setState is being used, it does a equality check before setState.
 * By default, we do a deep equality check but this can be customized.
 * @param initialState Initial value for the state
 * @param compare a comparison function
 * @returns [state, setState] where setState is modified to do equaility check
 */
function usePureState<State>(
  initialState: State,
  compare: (x: State, y: State) => boolean = isDeepEqual
) {
  const compareRef = useRef(compare);
  const [state, setState] = useState<State>(initialState);
  const isUnmounted = useIsUnmounted();

  useEffect(() => {
    compareRef.current = compare;
  }, [compare]);

  return [
    state,
    useCallback(
      (newState: State) => {
        if (isUnmounted) {
          return;
        }

        setState((prevState) => {
          if (!compareRef.current(prevState, newState)) {
            return newState;
          }
          return prevState;
        });
      },
      [isUnmounted]
    )
  ] as const;
}

export default usePureState;
